import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from './cx';
import styles from './Controls.module.css';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
}

export interface SegmentedProps<T extends string> {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  label?: string;
}

/** Pill tabs on a grey track; the active one is a white chip. */
export function Segmented<T extends string>({ value, options, onChange, size = 'sm', label }: SegmentedProps<T>) {
  return (
    <div className={styles.track} role="tablist" aria-label={label}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            className={cx(styles.segment, styles[size], active ? styles.segmentActive : undefined)}
            onClick={() => !active && onChange(option.value)}
          >
            {option.label}
            {option.count !== undefined ? <span className={styles.count}>{option.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children?: ReactNode;
  disabled?: boolean;
}

export function Switch({ checked, onChange, children, disabled }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={styles.switchRow}
      onClick={() => onChange(!checked)}
      disabled={disabled}
    >
      <span className={cx(styles.switchTrack, checked ? styles.switchOn : undefined)}>
        <span className={styles.switchThumb} />
      </span>
      {children}
    </button>
  );
}

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}

export function Checkbox({ checked, onChange, children }: CheckboxProps) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} className={styles.checkboxRow} onClick={() => onChange(!checked)}>
      <span className={cx(styles.box, checked ? styles.boxOn : undefined)}>{checked ? <Check size={12} strokeWidth={3.2} /> : null}</span>
      {children}
    </button>
  );
}

export interface RadioCardProps {
  selected: boolean;
  onSelect: () => void;
  title: string;
  text: string;
}

export function RadioCard({ selected, onSelect, title, text }: RadioCardProps) {
  return (
    <button type="button" role="radio" aria-checked={selected} className={cx(styles.radioCard, selected ? styles.radioCardOn : undefined)} onClick={onSelect}>
      <span className={cx(styles.radio, selected ? styles.radioOn : undefined)} />
      <span className={styles.radioText}>
        <span className={styles.radioTitle}>{title}</span>
        <span className={styles.radioHint}>{text}</span>
      </span>
    </button>
  );
}
